import { useEffect, useState } from "react";
import { api } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, FolderOpen, Download } from "lucide-react";

export const CustomerDocumentsCard = ({ customerId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!customerId) return;
    setLoading(true);
    api
      .get(`/api/customers/${customerId}/documents`)
      .then((res) => setDocuments(res.data || []))
      .catch(() => setDocuments([]))
      .finally(() => setLoading(false));
  }, [customerId]);

  if (!customerId) return null;
  if (!loading && documents.length === 0) return null;

  return (
    <Card data-testid="detail-customer-docs-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-amber-600" />
          Kundendokumente ({documents.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Lädt…</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {documents.map((doc) => {
              const isPdf =
                (doc.filename || "").toLowerCase().endsWith(".pdf") ||
                (doc.url || "").toLowerCase().includes(".pdf");
              const downloadUrl =
                doc.url && doc.url.includes("/upload/")
                  ? doc.url.replace("/upload/", "/upload/fl_attachment/")
                  : doc.url;
              return (
                <div
                  key={doc.id}
                  className="relative group rounded-lg border overflow-hidden"
                  data-testid={`detail-doc-${doc.id}`}
                >
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Im Browser öffnen"
                  >
                    {isPdf ? (
                      <div className="w-full h-24 bg-red-50 flex flex-col items-center justify-center text-red-700 hover:bg-red-100 transition-colors">
                        <FileText className="w-7 h-7" />
                        <span className="text-[10px] font-bold mt-0.5 tracking-wider">
                          PDF
                        </span>
                      </div>
                    ) : (
                      <img
                        src={doc.url}
                        alt={doc.filename}
                        className="w-full h-24 object-cover"
                      />
                    )}
                  </a>
                  <a
                    href={downloadUrl}
                    download={
                      doc.filename || (isPdf ? "dokument.pdf" : "dokument")
                    }
                    onClick={(e) => e.stopPropagation()}
                    title="Herunterladen"
                    className="absolute top-1 left-1 bg-slate-700 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-900"
                    data-testid={`detail-download-doc-${doc.id}`}
                  >
                    <Download className="w-3 h-3" />
                  </a>
                  <div className="p-1.5">
                    <p
                      className="text-xs font-medium truncate"
                      title={doc.filename}
                    >
                      {doc.filename || doc.doc_type}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {new Date(doc.created_at).toLocaleDateString("de-DE")}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
